import { useMutation } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { AxiosError } from "axios";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { Card } from "shared/components/Card";
import { DatePicker } from "shared/components/DatePickerCalendar";
import { getErrorMessage } from "utils/error";
import moment from "moment";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Calendar03Icon,
  InformationCircleIcon
} from "@hugeicons-pro/core-stroke-rounded";
import type { IconSvgElement } from "@hugeicons/react";

export const MoveInDateSlice = ({
  applicationId,
  onSuccess,
  moveInDate,
  availableAt
}: {
  applicationId: number;
  onSuccess?: () => void;
  moveInDate?: string | null | undefined;
  availableAt?: string | null | undefined;
}) => {
  const [selectedDate, setSelectedDate] = useState<string | undefined>(
    undefined
  );

  useEffect(() => {
    if (moveInDate) setSelectedDate(moveInDate);
  }, [moveInDate]);

  const today = moment().startOf("day");
  const parsedAvailableAt = availableAt ? moment(availableAt) : null;
  const isFutureAvailableAt =
    parsedAvailableAt !== null && parsedAvailableAt.isAfter(today, "day");
  const isNullAvailableAt = parsedAvailableAt === null;

  const minDate = useMemo(
    () =>
      isFutureAvailableAt && parsedAvailableAt
        ? parsedAvailableAt.format("YYYY-MM-DD")
        : today.format("YYYY-MM-DD"),
    [availableAt]
  );

  const { mutate: updateDesiredMoveInDate } = useMutation({
    mutationKey: ["APPLICATION_DESIRED_MOVE_IN"],
    mutationFn: (desiredMoveInDate: string) =>
      instance.patch(`/portal/v1/applications/${applicationId}`, {
        desired_move_in_date: desiredMoveInDate
      }),
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      if (error instanceof AxiosError && error.response?.status === 403) {
        toast.error(
          "You do not have permission to change desired move-in date"
        );
        return;
      }
      toast.error(getErrorMessage(error) || "Error");
    }
  });

  return (
    <div className="bg-background-900">
      <form
        className="max-w-[400px] mx-auto h-[calc(80vh-74px)] pt-6"
        onSubmit={(e) => {
          e.preventDefault();
          updateDesiredMoveInDate(selectedDate || "");
        }}
      >
        <Card>
          {isNullAvailableAt && (
            <div className="flex items-center gap-2 border-b border-alpha-100 bg-warning-50 p-4">
              <HugeiconsIcon
                icon={InformationCircleIcon as IconSvgElement}
                className="shrink-0 text-warning-700"
                size={24}
              />
              <p className="text-sm font-medium text-warning-700">
                Move in date will be confirmed by property manager. Final
                availability may change
              </p>
            </div>
          )}
          {isFutureAvailableAt && parsedAvailableAt && (
            <div className="flex items-center gap-2 border-b border-alpha-100 bg-success-50 p-4">
              <HugeiconsIcon
                icon={Calendar03Icon as IconSvgElement}
                className="text-success-700"
                size="24px"
              />
              <p className="text-sm font-medium text-success-700">
                Earliest available date:{" "}
                {parsedAvailableAt.format("MMM DD, YYYY")}
              </p>
            </div>
          )}
          <DatePicker
            selectedDate={selectedDate}
            minDate={minDate}
            onSelectDate={(e) => {
              setSelectedDate(e);
            }}
          />
        </Card>

        <div className="mt-4">
          <Button
            className="w-full mt-auto"
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.BASE}
            type="submit"
          >
            Save
          </Button>
        </div>
      </form>
    </div>
  );
};
